<template>
  <div class="asr-create-pick-call tw-relative tw-min-w-[820px] tw-max-w-[100%]">
    <div class="tw-mb-3 tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
      <CustomInput
        :value="searchTerm"
        :isSearch="true"
        placeholder="Поиск (ЖК, проект, подрядчик, список работ, тип помещения)"
        v-debounce:300="onSearchChange"
        class="tw-min-w-[260px]"
      />
      <span class="tw-text-grey-7 tw-text-sm">
        Групп: {{ filteredGroups.length }} · Вызовов: {{ filteredHelpCallsTotal }}
      </span>
    </div>

    <q-linear-progress v-if="isLoading" indeterminate />

    <div v-if="!isLoading && !filteredGroups.length" class="tw-py-8">
      <NotFoundText text="Принятых вызовов не найдено" />
    </div>

    <div
      v-if="!isLoading && filteredGroups.length"
      class="asr-create-pick-call__list tw-divide-y tw-divide-grey-4 tw-border tw-border-grey-4 tw-rounded"
    >
      <div
        v-for="row in filteredGroups"
        :key="row.groupKey"
        class="asr-create-pick-call__row tw-flex tw-items-stretch tw-gap-3 tw-p-3"
      >
        <div class="tw-flex-1 tw-min-w-0">
          <div
            class="tw-mt-0.5 tw-text-sm tw-text-grey-8 tw-whitespace-normal tw-break-words"
          >
            <div v-if="row.work_set_check_group_name" class="tw-font-medium">
              {{ row.work_set_check_group_name }}
            </div>
            <div v-if="row.placement_type_name" class="tw-mt-1 tw-text-grey-7">
              Тип помещения: {{ row.placement_type_name }}
            </div>
            <div class="tw-mt-1 tw-text-xs tw-text-grey-6">
              Вызовов в группе: {{ row.helpCallsCount }}
            </div>
          </div>
        </div>
        <div class="tw-flex tw-flex-shrink-0 tw-flex-col tw-items-end tw-gap-1">
          <q-btn
            unelevated
            no-caps
            color="primary"
            label="Сформировать АСР"
            :disable="!this.data?.contractorId"
            @click="onPickGroup(row)"
          />
          <span
            v-if="row.lastAsrHint"
            class="tw-text-xs tw-text-grey-7 tw-italic tw-text-right tw-max-w-[220px]"
          >
            {{ row.lastAsrHint }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CustomInput from "@/components/form/CustomInput";
import NotFoundText from "@/components/common/NotFoundText";
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";
import { useAppStore } from "@/services/store/app";
import { getCheckedCallsForAsr } from "@/views/ResidentialSettings/services";
import { MODAL_NAMES } from "@/constants";

export default {
  name: "AsrCreatePickCallNew",
  components: { CustomInput, NotFoundText },
  props: {
    data: { type: Object, required: true },
    closeModal: { type: Function, required: true },
  },
  data() {
    return {
      isLoading: false,
      calls: [],
      searchTerm: "",
    };
  },
  setup() {
    const { showErrorSnackbar, showSucceedSnackbar } = useCustomSnackbar();
    const appStore = useAppStore();
    return { showErrorSnackbar, showSucceedSnackbar, appStore };
  },
  computed: {
    filteredGroups() {
      const q = (this.searchTerm || "").toLowerCase().trim();
      if (!q) return this.calls;
      return this.calls.filter((r) =>
        [
          r.resident_name,
          r.project_name,
          r.contractor_name,
          r.work_set_check_group_name,
          r.placement_type_name,
          String(r.helpCallsCount ?? ""),
        ]
          .filter((x) => x != null && x !== "")
          .map((x) => String(x).toLowerCase())
          .some((s) => s.includes(q))
      );
    },
    filteredHelpCallsTotal() {
      return this.filteredGroups.reduce(
        (sum, g) => sum + (g.helpCallsCount || 0),
        0
      );
    },
  },
  mounted() {
    this.loadCalls();
  },
  methods: {
    async loadCalls() {
      this.isLoading = true;
      const rows = await getCheckedCallsForAsr({
        project_id: this.data?.projectId,
        project_entrance_id: this.data?.projectEntranceId,
      });
      this.isLoading = false;
      this.calls = this.buildGroupedCalls(Array.isArray(rows) ? rows : []);
    },
    onSearchChange(value) {
      this.searchTerm = value || "";
    },
    asrCallGroupKey(r) {
      const wsgId = r.work_set_check_group_id;
      const ptId = r.placement_type_id;
      if (
        wsgId != null &&
        wsgId !== "" &&
        ptId != null &&
        ptId !== ""
      ) {
        return `id:${wsgId}:${ptId}`;
      }
      const wsg = String(r.work_set_check_group_name ?? "").trim().toLowerCase();
      const pt = String(r.placement_type_name ?? "").trim().toLowerCase();
      return `n:${wsg}\0${pt}`;
    },
    entranceFloorLabel(levelNum, floorMapId) {
      const opts = this.data?.floorOptions;
      if (!Array.isArray(opts) || !opts.length) {
        return `Этаж ${levelNum}`;
      }
      const byLevel = opts.find(
        (f) => f != null && Number(f.floor) === Number(levelNum)
      );
      if (byLevel?.floor_name) return byLevel.floor_name;
      if (floorMapId != null && floorMapId !== "") {
        const byMap = opts.find(
          (f) => f != null && Number(f.floor_map_id) === Number(floorMapId)
        );
        if (byMap?.floor_name) return byMap.floor_name;
      }
      return `Этаж ${levelNum}`;
    },
    floorOptionsFromGroupRows(levelNumToFloorMeta) {
      if (!levelNumToFloorMeta?.size) return [];
      const list = Array.from(levelNumToFloorMeta.entries())
        .map(([levelNum, meta]) => {
          const floorMapId = meta?.floor_map_id ?? null;
          const base = this.entranceFloorLabel(levelNum, floorMapId);
          const docNum = meta?.linked_asr_document_num;
          const docDate = meta?.linked_asr_doc_date;
          const hasLinked =
            meta?.linked_asr_floor_map_document_id != null &&
            !Number.isNaN(Number(meta.linked_asr_floor_map_document_id));
          let suffix = "";
          if (hasLinked) {
            const numPart =
              docNum != null && String(docNum).trim() !== ""
                ? `связана АСР № ${String(docNum).trim()}`
                : "уже есть связанная АСР";
            const datePart =
              docDate != null && String(docDate).trim() !== ""
                ? ` (${String(docDate).trim()})`
                : "";
            suffix = ` — ${numPart}${datePart}`;
          }
          return {
            floor: levelNum,
            floor_label: `Этаж ${meta?.floor}`,
            floor_name: base + suffix,
            floor_map_id: floorMapId,
            has_linked_asr: hasLinked,
            linked_asr_floor_map_document_id:
              meta?.linked_asr_floor_map_document_id ?? null,
            linked_asr_document_num:
              docNum != null && String(docNum).trim() !== ""
                ? String(docNum).trim()
                : null,
            linked_asr_doc_date:
              docDate != null && String(docDate).trim() !== ""
                ? String(docDate).trim()
                : null,
          };
        })
        .sort((a, b) => Number(a.floor) - Number(b.floor));
      return list;
    },
    buildGroupedCalls(rows) {
      const map = new Map();
      for (const r of rows) {
        const groupKey = this.asrCallGroupKey(r);
        if (!map.has(groupKey)) {
          map.set(groupKey, {
            groupKey,
            work_set_check_group_name: r.work_set_check_group_name ?? "",
            placement_type_name: r.placement_type_name ?? "",
            resident_name: r.resident_name,
            project_name: r.project_name,
            contractor_name: r.contractor_name,
            helpCallLevelsMap: new Map(),
            helpCallLevelsMeta: new Map(),
            last_asr_dates: [],
            levelNumToFloorMeta: new Map(),
          });
        }
        const g = map.get(groupKey);
        if (r.help_call_id != null && r.help_call_id !== "") {
          const hid = Number(r.help_call_id);
          if (!Number.isNaN(hid)) {
            let ln = null;
            if (r.level_num != null && r.level_num !== "") {
              const n = Number(r.level_num);
              if (!Number.isNaN(n)) ln = n;
            }
            g.helpCallLevelsMap.set(hid, ln);
            const prev = g.helpCallLevelsMeta.get(hid) || {};
            const linkedId = r.linked_asr_floor_map_document_id;
            const rowHasLinked =
              linkedId != null &&
              linkedId !== "" &&
              !Number.isNaN(Number(linkedId));
            g.helpCallLevelsMeta.set(hid, {
              floor: r.floor,
              linked_asr_floor_map_document_id: rowHasLinked
                ? Number(linkedId)
                : prev.linked_asr_floor_map_document_id ?? null,
              linked_asr_document_num:
                rowHasLinked &&
                r.linked_asr_document_num != null &&
                String(r.linked_asr_document_num).trim() !== ""
                  ? String(r.linked_asr_document_num).trim()
                  : prev.linked_asr_document_num ?? null,
              linked_asr_doc_date:
                rowHasLinked &&
                r.linked_asr_doc_date != null &&
                String(r.linked_asr_doc_date).trim() !== ""
                  ? String(r.linked_asr_doc_date).trim()
                  : prev.linked_asr_doc_date ?? null,
            });
          }
        }
        if (r.last_asr_date) g.last_asr_dates.push(String(r.last_asr_date));
        if (r.level_num != null && r.level_num !== "") {
          const lv = Number(r.level_num);
          if (!Number.isNaN(lv)) {
            const prevMeta = g.levelNumToFloorMeta.get(lv) || {
              floor_map_id: null,
              linked_asr_floor_map_document_id: null,
              linked_asr_document_num: null,
              linked_asr_doc_date: null,
              floor: r.floor,
            };
            const floorMapId =
              r.floor_map_id != null && r.floor_map_id !== ""
                ? r.floor_map_id
                : prevMeta.floor_map_id;
            const linkedDocId = r.linked_asr_floor_map_document_id;
            const rowHasLinked =
              linkedDocId != null &&
              linkedDocId !== "" &&
              !Number.isNaN(Number(linkedDocId));
            g.levelNumToFloorMeta.set(lv, {
              floor_map_id: floorMapId ?? prevMeta.floor_map_id,
              linked_asr_floor_map_document_id: rowHasLinked
                ? Number(linkedDocId)
                : prevMeta.linked_asr_floor_map_document_id,
              linked_asr_document_num: rowHasLinked
                ? String(r.linked_asr_document_num || "").trim() ||
                  prevMeta.linked_asr_document_num
                : prevMeta.linked_asr_document_num,
              linked_asr_doc_date: rowHasLinked
                ? String(r.linked_asr_doc_date || "").trim() ||
                  prevMeta.linked_asr_doc_date
                : prevMeta.linked_asr_doc_date,
              floor: r.floor,
            });
          }
        }
      }
      return Array.from(map.values())
        .map((g) => {
          const help_call_ids = Array.from(g.helpCallLevelsMap.keys()).sort(
            (a, b) => Number(a) - Number(b)
          );
          const helpCallsWithFloors = Array.from(
            g.helpCallLevelsMap.entries()
          ).map(([help_call_id, level_num]) => {
            const m = g.helpCallLevelsMeta.get(help_call_id) || {};
            return {
              help_call_id,
              level_num,
              linked_asr_floor_map_document_id:
                m.linked_asr_floor_map_document_id ?? null,
              linked_asr_document_num: m.linked_asr_document_num ?? null,
              linked_asr_doc_date: m.linked_asr_doc_date ?? null,
              floor: m.floor,
            };
          });
          const uniqueDates = [...new Set(g.last_asr_dates)];
          let lastAsrHint = null;
          if (uniqueDates.length === 1) {
            lastAsrHint = `По вызовам уже есть АСР ${uniqueDates[0]}`;
          } else if (uniqueDates.length > 1) {
            lastAsrHint = "По части вызовов из группы уже есть АСР";
          }
          const floorOptions = this.floorOptionsFromGroupRows(
            g.levelNumToFloorMeta
          );
          return {
            groupKey: g.groupKey,
            work_set_check_group_name: g.work_set_check_group_name,
            placement_type_name: g.placement_type_name,
            resident_name: g.resident_name,
            project_name: g.project_name,
            contractor_name: g.contractor_name,
            help_call_ids,
            helpCallsWithFloors,
            helpCallsCount: help_call_ids.length,
            lastAsrHint,
            floorOptions,
          };
        })
        .filter((g) => g.helpCallsCount > 0);
    },
    onPickGroup(row) {
      if (!this.data?.contractorId) {
        this.showErrorSnackbar("Подрядчик проекта не назначен");
        return;
      }
      if (!row.help_call_ids?.length) {
        this.showErrorSnackbar("Нет вызовов для создания АСР");
        return;
      }
      const onSuccess = this.data?.onSuccess;
      this.closeModal();
      this.appStore.showModal(MODAL_NAMES.AsrCreateOrReform, {
        title: "Акт скрытых работ",
        mode: "create",
        helpCallId: row.help_call_ids[0],
        helpCallIds: row.help_call_ids,
        helpCallsWithFloors: row.helpCallsWithFloors || [],
        contractorId: this.data?.contractorId,
        residentId: this.data?.residentId,
        residentName: row.resident_name,
        contractorName: row.contractor_name,
        projectName: row.project_name,
        documentParams: this.data?.documentParams || {},
        floorOptions:
          row.floorOptions?.length > 0
            ? row.floorOptions
            : this.data?.floorOptions || [],
        enableAsrFloorRange: true,
        onSuccess: (payload) => {
          if (typeof onSuccess === "function") onSuccess(payload);
        },
      });
    },
  },
};
</script>

<style scoped lang="scss">
.asr-create-pick-call {
  &__list {
    max-height: 60vh;
    overflow-y: auto;
  }

  &__row {
    transition: background-color 0.15s ease;
    &:hover {
      background-color: #f5f7fa;
    }
  }
}
</style>
